import { Icon, IconButton } from '@elements';
import { Box } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import EmailIcon from '@images/email-icon.svg';
import DownloadIcon from '@images/download-icon.svg';
import TableIcon from '@images/table-icon.svg';
import EmailIconDisabled from '@images/email-icon-disabled.svg';
import DownloadIconDisabled from '@images/download-icon-disabled.svg';
import ReturnTicketsIcon from '@images/return-tickets.svg';
import ReturnTicketsIconDisabled from '@images/return-tickets-disabled.svg';
import TableIconDisabled from '@images/table-icon-disabled.svg';
import useDownloadTicket from '@hooks/ticket/useDownloadTicket';
import useSendToEmailTicket from '@hooks/ticket/useSendToEmailTicket';
import SeasonTicketEventsModal from '@components/modals/SeasonTicketEventsModal';
import useGetWallet from '@hooks/ticket/useGetWallet';
import useRefundTickets from '@hooks/ticket/useRefundTickets';
import RefundTicketModal from '@components/modals/RefundTicketModal';
import RefundSuccessModal from '@components/modals/RefundSuccessModal';
import WalletIcon from '@images/wallet-icon.svg';
import WalletIconDisabled from '@images/wallet-icon-disabled.svg';
import { ErrorDialogContext } from '@contexts/ErrorDialogContext';
import Loader from '@components/loader/Loader';
import { getErrorMessageFromAxiosError } from '@utils/getErrorMessageFromAxiosError';

type Props = {
  disabled?: boolean;
  eventSessionId: number;
  orderUuid?: string;
  ticketUuid: string;
  onRefundSuccess?: (ticketUuid: string) => void;
};

const Actions = ({ disabled, ticketUuid, eventSessionId, orderUuid, onRefundSuccess }: Props) => {
  const downloadTicket = useDownloadTicket(ticketUuid);
  const sendToEmailTicket = useSendToEmailTicket(ticketUuid);
  const getWallet = useGetWallet(ticketUuid);
  const refundTicket = useRefundTickets(orderUuid ?? '');
  const [refundModalOpen, setRefundModalOpen] = React.useState(false);
  const [refundSuccessOpen, setRefundSuccessOpen] = React.useState(false);
  const errorDialogContext = useContext(ErrorDialogContext);

  const [eventsModalOpen, setEventsModalOpen] = React.useState(false);

  useEffect(() => {
    const error =
      downloadTicket.error ||
      sendToEmailTicket.error ||
      getWallet.error ||
      refundTicket.error;
      
      if (error) {
      errorDialogContext.setErrorMessage(getErrorMessageFromAxiosError(error));
      errorDialogContext.setOpenErrorDialog(true);
    }
  }, [
    downloadTicket.error,
    sendToEmailTicket.error,
    getWallet.error,
    refundTicket.error,
  ]);

  const handleClickOpen = () => {
    setEventsModalOpen(true);
  };

  const handleClose = () => {
    setEventsModalOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <IconButton
        sx={{
          px: '10px',
          backgroundColor: '#F9F9F9',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          '&:hover': {
            backgroundColor: '#E0E0E0',
          },
        }}
        disabled={disabled || sendToEmailTicket.isPending}
        onClick={() => sendToEmailTicket.mutate()}
      >
        <Icon
          src={disabled ? EmailIconDisabled : EmailIcon}
          width={28}
          height={28}
        />
      </IconButton>
      <IconButton
        sx={{
          px: '10px',
          backgroundColor: '#F9F9F9',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: '#E0E0E0',
          },
        }}
        disabled={disabled || downloadTicket.isPending}
        onClick={() => downloadTicket.mutate()}
      >
        <Icon
          src={disabled ? DownloadIconDisabled : DownloadIcon}
          width={28}
          height={28}
        />
      </IconButton>
      <IconButton
        sx={{
          px: '10px',
          backgroundColor: '#F9F9F9',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: '#E0E0E0',
          },
        }}
        onClick={() => handleClickOpen()}
      >
        <Icon
          src={disabled ? TableIconDisabled : TableIcon}
          width={28}
          height={28}
        />
      </IconButton>
      <IconButton
        sx={{
          px: '10px',
          backgroundColor: '#F9F9F9',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: '#E0E0E0',
          },
        }}
        disabled={disabled || refundTicket.isPending || !orderUuid}
        onClick={() => setRefundModalOpen(true)}
      >
        <Icon
          src={
            disabled || refundTicket.isPending || !orderUuid
              ? ReturnTicketsIconDisabled
              : ReturnTicketsIcon
          }
          width={28}
          height={28}
        />
      </IconButton>
      <IconButton
        sx={{
          px: '10px',
          backgroundColor: '#F9F9F9',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          '&:hover': {
            backgroundColor: '#E0E0E0',
          },
        }}
        disabled={disabled}
        onClick={() => getWallet.mutate()}
      >
        <Icon
          src={
            disabled || getWallet.isPending ? WalletIconDisabled : WalletIcon
          }
          width={28}
          height={28}
        />
      </IconButton>

      {eventsModalOpen && (
        <SeasonTicketEventsModal
          isOpen
          closeHandler={handleClose}
          eventSessionId={eventSessionId}
          ticketUuid={ticketUuid}
        />
      )}
      <RefundTicketModal
        isOpen={refundModalOpen}
        closeHandler={() => setRefundModalOpen(false)}
        acceptHandler={() => {
          refundTicket.mutate([ticketUuid], {
            onSuccess: () => {
              setRefundModalOpen(false);
              setRefundSuccessOpen(true);
              onRefundSuccess?.(ticketUuid);
            },
            onError: () => {
              setRefundModalOpen(false);
            },
          });
        }}
        title="Вы хотите вернуть абонемент?"
      />
      <RefundSuccessModal
        isOpen={refundSuccessOpen}
        closeHandler={() => setRefundSuccessOpen(false)}
        message="Абонемент возвращен, денежные средства поступят в течение 14 дней"
      />
      <Loader
        isShowLoader={
          downloadTicket.isPending ||
          sendToEmailTicket.isPending ||
          getWallet.isPending ||
          refundTicket.isPending
        }
      />
    </Box>
  );
};

export default Actions;
